import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ClientApi } from '../../../apis';
import { toast } from 'react-toastify';

// Validation schema using Yup
const validationSchema = Yup.object({
  clientNickname: Yup.string().required('Client nickname is required'),
  company: Yup.string().required('Company is required'),
  contactNumber: Yup.string(),
  contactPerson: Yup.string().required('Contact person is required'),
  contactTitle: Yup.string(),
  contactAddress: Yup.string(),
  phone: Yup.string()
    .matches(/^[\d\s\-\+\(\)]+$/, 'Invalid phone number format')
    .min(10, 'Phone number must be at least 10 digits')
    .required('Phone is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  billingAddress: Yup.string().required('Billing address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, 'Invalid zip code format')
    .required('Zip code is required'),
  billingCity: Yup.string().required('Billing city is required'),
  billingState: Yup.string().required('Billing state is required'),
  billingZipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, 'Invalid zip code format')
    .required('Billing zip code is required'),
  accountsPayableEmail: Yup.string()
    .email('Invalid email address')
    .required('Accounts payable email is required'),
  notes: Yup.string(),
});

function AddClient() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      clientNickname: '',
      company: '',
      contactNumber: '',
      contactPerson: '',
      contactTitle: '',
      contactAddress: '',
      phone: '',
      email: '',
      billingAddress: '',
      city: '',
      state: '',
      zipCode: '',
      billingCity: '',
      billingState: '',
      billingZipCode: '',
      accountsPayableEmail: '',
      notes: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Ensure contactNumber is populated (backend requires it)
        const payload = {
          ...values,
          contactNumber: values.contactNumber || values.phone,
        };
        const response = await ClientApi.createClient(payload);

        if (response.status === 200 || response.status === 201) {
          toast.success('Client added successfully!');
          navigate('/admin/clients');
        }
      } catch (error) {
        console.error('Error creating client:', error);
        toast.error(error.response?.data?.message || 'Failed to create client');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom mb={2}>
        Add Client
      </Typography>

      <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }}>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            '& .MuiTextField-root': { mb: 2 },
          }}
        >
          {/* columnSpacing applies a negative left margin (-8px per unit). At the mobile Paper padding that exceeded the gutter and pushed the grid past the left edge, so the page looked off-centre. Items are xs={12} on a phone anyway, so there is no column gap to keep there. */}
          <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }}>
            {/* Client Nickname */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Client Nickname
              </Typography>
              <TextField
                fullWidth
                id="clientNickname"
                name="clientNickname"
                placeholder="Enter client nickname"
                value={formik.values.clientNickname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.clientNickname && Boolean(formik.errors.clientNickname)}
                helperText={formik.touched.clientNickname && formik.errors.clientNickname}
              />
            </Grid>

            {/* CONTACT Section */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                CONTACT
              </Typography>
            </Grid>

            {/* Contact Person & Contact Title */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Person
              </Typography>
              <TextField
                fullWidth
                id="contactPerson"
                name="contactPerson"
                placeholder="Enter contact person"
                value={formik.values.contactPerson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactPerson && Boolean(formik.errors.contactPerson)}
                helperText={formik.touched.contactPerson && formik.errors.contactPerson}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Title
              </Typography>
              <TextField
                fullWidth
                id="contactTitle"
                name="contactTitle"
                placeholder="Enter contact title"
                value={formik.values.contactTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactTitle && Boolean(formik.errors.contactTitle)}
                helperText={formik.touched.contactTitle && formik.errors.contactTitle}
              />
            </Grid>

            {/* Contact Email & Contact Phone */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Email
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                placeholder="Enter contact email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Number
              </Typography>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                placeholder="Enter contact phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>

            {/* Contact Address & City */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Address
              </Typography>
              <TextField
                fullWidth
                id="contactAddress"
                name="contactAddress"
                placeholder="Enter contact address"
                value={formik.values.contactAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactAddress && Boolean(formik.errors.contactAddress)}
                helperText={formik.touched.contactAddress && formik.errors.contactAddress}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                City
              </Typography>
              <TextField
                fullWidth
                id="city"
                name="city"
                placeholder="Enter city"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
              />
            </Grid>

            {/* State & Zip Code */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                State
              </Typography>
              <TextField
                fullWidth
                id="state"
                name="state"
                placeholder="Enter state"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Zip Code
              </Typography>
              <TextField
                fullWidth
                id="zipCode"
                name="zipCode"
                placeholder="Enter zip code"
                value={formik.values.zipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
                helperText={formik.touched.zipCode && formik.errors.zipCode}
              />
            </Grid>

            {/* BILLING Section */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                BILLING
              </Typography>
            </Grid>

            {/* Company Name & Accounts Payable Email */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Company Name
              </Typography>
              <TextField
                fullWidth
                id="company"
                name="company"
                placeholder="Enter company name"
                value={formik.values.company}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Accounts Payable Email
              </Typography>
              <TextField
                fullWidth
                id="accountsPayableEmail"
                name="accountsPayableEmail"
                placeholder="Enter AP email"
                value={formik.values.accountsPayableEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountsPayableEmail && Boolean(formik.errors.accountsPayableEmail)}
                helperText={formik.touched.accountsPayableEmail && formik.errors.accountsPayableEmail}
              />
            </Grid>

            {/* Billing Address & Billing City */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing Address
              </Typography>
              <TextField
                fullWidth
                id="billingAddress"
                name="billingAddress"
                placeholder="Enter billing address"
                value={formik.values.billingAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingAddress && Boolean(formik.errors.billingAddress)}
                helperText={formik.touched.billingAddress && formik.errors.billingAddress}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing City
              </Typography>
              <TextField
                fullWidth
                id="billingCity"
                name="billingCity"
                placeholder="Enter billing city"
                value={formik.values.billingCity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingCity && Boolean(formik.errors.billingCity)}
                helperText={formik.touched.billingCity && formik.errors.billingCity}
              />
            </Grid>

            {/* Billing State & Billing Zip Code */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing State
              </Typography>
              <TextField
                fullWidth
                id="billingState"
                name="billingState"
                placeholder="Enter billing state"
                value={formik.values.billingState}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingState && Boolean(formik.errors.billingState)}
                helperText={formik.touched.billingState && formik.errors.billingState}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing Zip Code
              </Typography>
              <TextField
                fullWidth
                id="billingZipCode"
                name="billingZipCode"
                placeholder="Enter billing zip code"
                value={formik.values.billingZipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingZipCode && Boolean(formik.errors.billingZipCode)}
                helperText={formik.touched.billingZipCode && formik.errors.billingZipCode}
              />
            </Grid>

            {/* NOTES Section */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                NOTES
              </Typography>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                multiline
                rows={6}
                placeholder="Enter any additional notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={2}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/clients')}
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{ minWidth: 120, px: 4 }}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default AddClient;

